import React, {Suspense, useCallback, useContext, useEffect, useRef, useState} from 'react';
import FacadeFailureLayerInfo from '@core/scripts/components/layers/FacadeFailureLayer.jsx';
import HintWithIcon from '@bonprix/pattern-library/templates/hint/with-icon';
import Breadcrumb from '../header/Breadcrumb.jsx';
import SearchAlternatives from './SearchAlternatives.jsx';
import AlpiList from './AlpiList.jsx';
import Pagination from './Pagination.jsx';
import Device from '@core/scripts/components/device';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {Constants} from '../view-constants';
import Landmark from '@core/scripts/components/landmark-adapter';
import SeoContent from '../footer/SeoContent.jsx';
import BackToTopButton from './BackToTopButton.jsx';
import {
    ArticleOverviewTeaserContent
} from '@teaser/scripts/components/article-overview-teaser-content/ArticleOverviewTeaserContent.jsx';
import {useDispatch, useSelector} from 'react-redux';

import {
    selectArticleAjaxCall,
    selectContentAjaxCall,
    selectFilterAjaxCall,
    setArticleAjaxCall,
    setContentAjaxCallPopstate,
} from '../AjaxCallSlice.jsx';
import JSONParser from '@core/scripts/helper/json-parser';
import useEffectOnUpdate from '../helper/hooks-helper';
import {loadData} from '../helper/data-loader';
import {loadOff, loadOffFilter, loadOn} from '../LoaderSlice.jsx';
import History from '@core/scripts/components/history';
import {createUrlFromCurrentUrl, getContentAjaxUrl, pushUrlToHistory} from '../ArticleOverviewHelper';
import HeaderWrapper from '../header/HeaderWrapper.jsx';
import FilterOrderingButton from '../header/FilterOrderingButton.jsx';
import FilterTokens from '../header/FilterTokens.jsx';
import {useTrinity} from '../hooks/use-trinity.js';
import {useBreakpointUp} from "bonprix-design-system";

const RecommendationContainer = React.lazy(() => import('../footer/RecommendationContainer.jsx'));

export default function ArticleOverviewContent({
    givenArticleContent,
    givenSeoContent,
    givenResponseLimitations,
    givenHeaderContent,
    givenSearchResultContent,
    givenFilterContent,
    givenLandmarkContent,
    givenShopContent,
    givenRecommendationContent,
    givenNavigationContent,
    pointOfSaleId,
    givenTeaserContent,
    givenSearchContext,
    givenConfErrorCase,
    givenRankingTrackingValue,
}) {
    const {
        texts,
        facadeFailure,
        chunkSize,
        numberOfChunks,
        contentAjaxUrl,
        articleAjaxUrl,
        prettyUrl,
        currentSearchParams,
        shopId,
        currentUrlPath,
        currentCategoryPath,
    } = useContext(articleOverviewStaticContext);
    const [allChunksLoaded, setAllChunksLoaded] = useState(false);

    const [filterContent, setFilterContent] = useState(givenFilterContent);
    const [articleContent, setArticleContent] = useState(givenArticleContent);
    const [overviewContent, setOverviewContent] = useState({
        navigationContent: givenNavigationContent,
        headerContent: givenHeaderContent,
        landmarkContent: givenLandmarkContent,
        shopContent: givenShopContent,
        searchResultContent: givenSearchResultContent,
        seoContent: givenSeoContent,
        responseLimitations: givenResponseLimitations,
        recommendationContent: givenRecommendationContent,
        teaserContent: givenTeaserContent,
        errorCase: givenConfErrorCase,
        rankingTrackingValue: givenRankingTrackingValue,
    });
    const navigationContent = overviewContent.navigationContent;
    const headerContent = overviewContent.headerContent;
    const landmarkContent = overviewContent.landmarkContent;
    const shopContent = overviewContent.shopContent;
    const searchResultContent = overviewContent.searchResultContent;
    const seoContent = overviewContent.seoContent;
    const responseLimitations = overviewContent.responseLimitations;
    const recommendationContent = overviewContent.recommendationContent;
    const searchContext = givenSearchContext;

    const dispatch = useDispatch();

    const filterAjaxCall = useSelector(selectFilterAjaxCall);
    const contentAjaxCall = useSelector(selectContentAjaxCall);
    const articleAjaxCall = useSelector(selectArticleAjaxCall);
    const breakpointLUp = useBreakpointUp('l');

    const {sendFilterView} = useTrinity();

    const contentLoadedCounter = useRef(0);
    const filterContentCopy = useRef(givenFilterContent);

    useEffect(() => {
        sendPageLandmark();
        sendFastFilterViewEvent();
        document.body.classList.add('filter-test-group');
    }, []);

    useEffectOnUpdate(() => {
        if (filterAjaxCall.path) {
            const newFilterContent = loadData(filterAjaxCall.path);
            return newFilterContent.then((result) => {
                if (result) {
                    dispatch(loadOffFilter());
                    return updateFilterModel(result, filterAjaxCall.isRange);
                }
                dispatch(loadOffFilter());
            });
        } else {
            setFilterContent(filterContentCopy.current);
        }
    }, [filterAjaxCall]);

    useEffectOnUpdate(() => {
        if (contentAjaxCall.path && contentAjaxCall.get) {
            const newFilterContent = loadData(contentAjaxCall.path);
            newFilterContent.then((result) => {
                if (result) {
                    updateContent(result, contentAjaxCall.popstate);
                }
            });
        } else {
            setTimeout(() => dispatch(loadOff()), 250);
        }
    }, [contentAjaxCall]);

    useEffectOnUpdate(() => {
        if (articleAjaxCall.path) {
            const newFilterContent = loadData(articleAjaxCall.path);
            newFilterContent.then((result) => {
                if (result) {
                    receiveChunks(result);
                }
            });
        }
    }, [articleAjaxCall]);

    useEffect(() => {
        window.addEventListener('popstate', () => {
            const category = 'kategorie/';
            const currentUrl = createUrlFromCurrentUrl(searchContext);
            if (currentUrl.hash !== '#menu') {
                const pathname = currentUrl.pathname;
                const prettyUrlIncludingSeoFilter = pathname.substring(
                    pathname.indexOf(category) + category.length,
                    pathname.length - 1,
                ); //e.g. "/de_de/kategorie/damen-mode-kleider/rot_42/" -> damen-mode-kleider/rot_42

                // TODO: Test
                const call = getContentAjaxUrl(
                    prettyUrl.current ? prettyUrlIncludingSeoFilter : '',
                    currentUrl.search,
                    contentAjaxUrl,
                );

                dispatch(loadOn());
                dispatch(setContentAjaxCallPopstate(call));
            }
        });
        return () => {};
    }, []);

    const updateAndGetModels = useCallback((payload) => {
        const modelJson = JSONParser.tryParseJSON(payload.model);
        const updatedModel = {
            navigationContent: modelJson.navigationContent,
            headerContent: modelJson.headerContent,
            landmarkContent: modelJson.landmarkContent,
            shopContent: modelJson.shopContent,
            searchResultContent: modelJson.searchResultContent,
            seoContent: modelJson.seoContent,
            responseLimitations: modelJson.responseLimitations,
            recommendationContent: modelJson.recommendationContent,
            teaserContent: modelJson.teaserContent,
            errorCase: modelJson.configurationContent.errorCase,
            rankingTrackingValue: modelJson.rankingTrackingValue,
        };
        const articleContent = modelJson.articleContent;
        articleContent.contentLoadedCounter = ++contentLoadedCounter.current;
        setArticleContent(articleContent);
        setFilterContent(modelJson.filterContent);
        filterContentCopy.current = modelJson.filterContent;
        setOverviewContent(updatedModel);

        return modelJson;
    });

    const receiveChunks = useCallback(
        (payload) => {
            const newAlpiItems = JSONParser.tryParseJSON(payload.model);
            const currentArticleContent = JSON.parse(JSON.stringify(articleContent));
            const currentAlpiItems = currentArticleContent.articleListPageItems;

            currentArticleContent.articleListPageItems = mergeAlpiLists(currentAlpiItems, newAlpiItems);

            setArticleContent(currentArticleContent);
        },
        [articleContent, setArticleContent],
    );

    const loadChunkRange = useCallback((chunkFrom, chunkTo, lastAlpiPosition) => {
        const params = new URLSearchParams(currentSearchParams.current);
        const shopIdFormatted = shopId.current ? `/${shopId.current}/` : '/';

        params.set(Constants.SEARCH_PARAMS_CHUNK_FROM, chunkFrom);
        params.set(Constants.SEARCH_PARAMS_CHUNK_TO, chunkTo);
        params.set(Constants.SEARCH_PARAMS_LAST_ALPI_POSITION, lastAlpiPosition);

        const articleAjaxCall = articleAjaxUrl + shopIdFormatted + '?' + params.toString();

        dispatch(setArticleAjaxCall(articleAjaxCall));
    }, []);

    function mergeAlpiLists(oldList, newList) {
        let currentIndex = oldList.findIndex((alpi) => {
            return alpi.gridItem.displayType === Constants.GRIDITEM_FILLER_ARTICLE;
        });
        newList.map((gridItem) => {
            let inserted = false;
            do {
                const currentGridItem = oldList[currentIndex].gridItem;
                if (currentGridItem.displayType === Constants.GRIDITEM_FILLER_ARTICLE) {
                    oldList[currentIndex].gridItem = gridItem;
                    inserted = true;
                }
                currentIndex++;
            } while (!inserted);
        });
        return oldList;
    }

    const navigateToProduct = useCallback((productUrl, positionIndex) => {
        const currentSearchParamsCopy = new URLSearchParams(currentSearchParams.current);
        currentSearchParamsCopy.set(Constants.SEARCH_PARAMS_ANCHOR, `${positionIndex}`);

        currentSearchParams.current = currentSearchParamsCopy;
        replaceCurrentUrlWithCurrentParams();
        // timeout is necessary to ensure that previous url manipulation is done (timeout in history.js)
        setTimeout(() => {
            window.location.assign(productUrl);
        });
    }, []);

    const updateScrollPosition = useCallback((alpiIndex) => {
        const currentSearchParamsCopy = new URLSearchParams(currentSearchParams.current);
        currentSearchParamsCopy.set(Constants.SEARCH_PARAMS_ANCHOR, `${alpiIndex}`);
        currentSearchParams.current = currentSearchParamsCopy;
        replaceCurrentUrlWithCurrentParams();
    }, []);

    function replaceCurrentUrlWithCurrentParams() {
        history.replaceState({}, '', currentUrlPath.current + '?' + currentSearchParams.current.toString());
    }

    function updateContent(result, popstate = false) {
        const newContent = updateAndGetModels(result);
        prettyUrl.current = newContent.shopContent?.prettyUrl;
        shopId.current = newContent.shopContent?.id;
        if (!newContent.hits || newContent.configurationContent.errorCase === 'NO_ARTICLES') {
            // normally the loader is turned off during alpi rendering. Since no alpis are rendered if there are no hits, turn off the loader here
            dispatch(loadOff());
        }

        if (popstate) {
            const currentUrl = createUrlFromCurrentUrl(searchContext);
            const sanitizedUrl = newContent.sanitizedUrl;
            const newUrl = sanitizedUrl ? new URL(currentUrl.origin + sanitizedUrl) : new URL(currentUrl);
            //TODO MAKE ONE CALL
            currentCategoryPath.current = prettyUrl.current;
            currentUrlPath.current = newUrl.pathname;
            currentSearchParams.current = newUrl.searchParams;
            return;
        }

        if (newContent.seoOptimizedUrl) {
            History.pushURL(newContent.seoOptimizedUrl, '');
        } else {
            pushUrlToHistory('', currentUrlPath.current, null, currentSearchParams.current);
        }
    }

    const updateFilterModel = useCallback(
        (payload, isRangeFilter) => {
            const modelJson = JSONParser.tryParseJSON(payload.model);
            if (!modelJson.hits) {
                // get filter names from filterContentCopy as they are not available if there are no hits
                modelJson.filterGroups.forEach((filterGroup) => {
                    const matchingGroup = filterContentCopy.current.filterGroups.find(
                        (copyGroup) => copyGroup.identifier === filterGroup.identifier,
                    );
                    if (matchingGroup) {
                        filterGroup.name = matchingGroup.name;
                    }
                });
            }
            if (!modelJson.hits && isRangeFilter) {
                return;
            }
            setFilterContent(modelJson);

            return modelJson.hits;
        },
        [setFilterContent],
    );

    const articleOverviewClasses = `article-overview__wrapper ${Device.isApp() ? '-is-app' : ''}`;
    const filledArticleList = articleContent.articleListPageItems.length > 0;
    const noArticlesError = overviewContent.errorCase === 'NO_ARTICLES';
    const responseLimitationHint =
        responseLimitations?.includes(Constants.RESPONSE_LIMITATION_INACCURATE_PRODUCTS) ||
        responseLimitations?.includes(Constants.RESPONSE_LIMITATION_INACCURATE_PRODUCTS_SELECTED);

    const rootShopName = headerContent.breadcrumbs?.items.find(
        (item) => item.shopId === shopContent?.filterRootShopId,
    )?.name;

    const checkIfAllChunksAreLoaded = useCallback(
        (chunk) => {
            const chunksForHits = Math.ceil(filterContent.hits / chunkSize);
            setAllChunksLoaded(chunk === numberOfChunks || chunk === chunksForHits);
        },
        [filterContent.hits, chunkSize, numberOfChunks, setAllChunksLoaded],
    );

    const shouldRenderBreadcrumbs =
        headerContent.breadcrumbs && ((shopContent && !Device.isApp()) || searchResultContent);

    return (
        <>
            {overviewContent.errorCase !== 'NO_ERROR' && (
                <FacadeFailureLayerInfo
                    key={`article-overview__facade-failure_${new Date().getTime()}`}
                    texts={facadeFailure}
                />
            )}

            {responseLimitationHint && (
                <HintWithIcon className="article-overview__response-limitation-hint" icon="hint">
                    {texts.responseLimitation}
                </HintWithIcon>
            )}

            <div className={articleOverviewClasses}>
                <div className="article-overview">
                    <div className={'article-overview__header' + (breakpointLUp ? '-sticky' : '')}>
                        <div className={'article-overview__header-first-row'}>
                            {shouldRenderBreadcrumbs && <Breadcrumb breadcrumbs={headerContent.breadcrumbs} />}
                            {breakpointLUp && (
                                <div className="article-overview__header-filter-btn">
                                    <FilterOrderingButton filterViewValue={landmarkContent?.filterViewLandmark} />
                                </div>
                            )}
                        </div>

                        {breakpointLUp && (
                            <HeaderWrapper
                                filterContent={filterContent}
                                landmarkContent={landmarkContent}
                                ordering={articleContent.ordering}
                                filledArticleList={filledArticleList}
                                showOnlyTokens={!filledArticleList && !noArticlesError}
                                rootShopName={rootShopName}
                            />
                        )}
                    </div>
                    {!breakpointLUp && (
                        <HeaderWrapper
                            filterContent={filterContent}
                            landmarkContent={landmarkContent}
                            ordering={articleContent.ordering}
                            filledArticleList={filledArticleList}
                            showOnlyTokens={!filledArticleList && !noArticlesError}
                            rootShopName={rootShopName}
                        />
                    )}

                    <FilterTokens
                        filterTokens={filterContent.filterTokens}
                        shopIdFilterGroup={filterContent.shopIdFilterGroup}
                    />

                    {overviewContent.teaserContent && (
                        <div
                            className="article-overview__teaser-content"
                            style={{height: `${overviewContent.teaserContent.height}px`}}
                        >
                            <ArticleOverviewTeaserContent model={overviewContent.teaserContent} />
                        </div>
                    )}

                    {headerContent.hintText && (
                        <HintWithIcon className="article-overview__hint-headline" icon="hint">
                            <span className="hint__message-bold">{headerContent.hintText.headline}</span>
                            <div className="article-overview__hint-message">{headerContent.hintText.message}</div>
                        </HintWithIcon>
                    )}

                    {searchResultContent?.searchAlternatives && (
                        <SearchAlternatives
                            searchAlternatives={searchResultContent.searchAlternatives}
                            searchQuery={searchResultContent.searchQuery}
                        />
                    )}

                    <div className="article-overview__sticky-article-filter-wrapper">
                        {/*TODO: TOO MANY RENDERS FIXEN UND KEY??*/}
                        {filledArticleList ? (
                            <AlpiList
                                alpiList={articleContent.articleListPageItems}
                                shopContent={shopContent}
                                filterTrackingValue={filterContent.filterTrackingValue}
                                rankingTrackingValue={overviewContent.rankingTrackingValue}
                                pointOfSaleId={pointOfSaleId}
                                checkChunks={checkIfAllChunksAreLoaded}
                                actualPageSize={articleContent.actualPageSize}
                                loadChunkRange={loadChunkRange}
                                navigateToProduct={navigateToProduct}
                                updateScrollPosition={updateScrollPosition}
                                contentLoadedCounter={articleContent.contentLoadedCounter}
                                teaserContent={overviewContent.teaserContent}
                            />
                        ) : noArticlesError ? (
                            <></>
                        ) : (
                            <HintWithIcon className="alpi-list__empty-hint" icon="hint">
                                <span className="hint__message-bold">{texts.filter_noHitsPart1}</span>
                                {texts.filter_noHitsPart2}
                            </HintWithIcon>
                        )}
                    </div>

                    {navigationContent.pagination.paginationItems.length > 0 && (
                        <Pagination pagination={navigationContent.pagination} isShopContent={shopContent !== null} />
                    )}
                </div>
            </div>

            <BackToTopButton />

            {recommendationContent && (
                <div className="recommendation-carousel__wrapper">
                    <Suspense fallback={<div></div>}>
                        <RecommendationContainer
                            key={recommendationContent.recommendationData}
                            ajaxSwiperData={recommendationContent.recommendationData}
                            loadingEnabled={allChunksLoaded}
                        />
                    </Suspense>
                </div>
            )}

            <SeoContent seoContent={seoContent} />
        </>
    );

    function sendPageLandmark() {
        if (landmarkContent?.pageLandmark) {
            Landmark.sendPageLandmark(JSON.parse(landmarkContent.pageLandmark));
        }
    }

    function sendFastFilterViewEvent() {
        if (filterContent?.shopIdFilterGroup) {
            const shopIdFilterGroupTrackingId = filterContent.shopIdFilterGroup.filters[0]?.trackingId;
            sendFilterView(
                'fast_filter',
                `${shopIdFilterGroupTrackingId}|`,
                Constants.FILTER_VIEW_EVENT_VERSION_TESTGROUP,
            );
        }
    }
}
